// src/components/admin/UsersTab.js
import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';

const UsersTab = ({ usersData, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [userHistory, setUserHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Fetch history only when a user is selected for editing
    useEffect(() => {
        if (editingUser?.uid) {
            fetchUserHistory(editingUser.uid);
        }
    }, [editingUser?.uid]);

    const fetchUserHistory = async (uid) => {
        setLoadingHistory(true);
        try {
            const res = await apiClient.get(`/admin/users/${uid}/withdrawals`);
            setUserHistory(res.data);
        } catch (err) { console.error(err); }
        setLoadingHistory(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put(`/admin/users/${editingUser.uid}`, editingUser);
            alert("User fully updated!");
            setEditingUser(null);
            onRefresh();
        } catch (err) { alert("Error: " + err.message); }
    };

    const handleDelete = async (uid, name) => {
        if (window.confirm(`⚠️ DELETE EVERYTHING? This deletes ${name} from Database AND Auth. They will be able to signup as a new user after this.`)) {
            try {
                await apiClient.delete(`/admin/users/${uid}`);
                alert("Account wiped successfully.");
                onRefresh();
            } catch (err) { alert(err.message); }
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put(`/admin/users/${editingUser.uid}`, editingUser);
            alert("User updated!");
            setEditingUser(null);
            onRefresh();
        } catch (err) {
            alert("Update failed.");
        }
    };

    // Filter Logic
    const filteredUsers = usersData.filter(user => {
        const search = searchTerm.toLowerCase();
        
        // Fields to search: Name, Email, Phone, State, District, Gender
        return (
            (user.name?.toLowerCase() || '').includes(search) ||
            (user.email?.toLowerCase() || '').includes(search) ||
            (user.phoneNumber || '').includes(search) ||
            (user.location?.state?.toLowerCase() || '').includes(search) ||
            (user.location?.district?.toLowerCase() || '').includes(search) ||
            (user.gender?.toLowerCase() || '') === search
        );
    });

    return (
        <div className="admin-tab-content">
            <input 
                type="text" placeholder="Search..." className="admin-search-bar"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
                    <table className="admin-table">
                <thead>
                    <tr>
                        <th>Basic Info</th>
                        <th>Location & Bio</th>
                        <th>Wallet</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredUsers.map(user => (
                        <tr key={user.uid}>
                            <td>
                                <strong>{user.name}</strong><br/>
                                <small>{user.email}</small><br/>
                                <small>{user.phoneNumber}</small>
                            </td>
                            <td>
                                {user.location?.state}, {user.location?.district}<br/>
                                <small>{user.gender} | Age: {user.age}</small>
                            </td>
                            <td>
                                <span style={{color: 'green'}}>₹{user.walletBalance || 0}</span><br/>
                                <small>Coins: {user.coins || 0}</small>
                            </td>
                            <td>{user.isBusiness ? '💼 Business' : '👤 User'}</td>
                            <td>
                                <button className="edit-post-button" onClick={() => setEditingUser(user)}>Edit</button>
                                <button className="delete-post-button" onClick={() => handleDelete(user.uid, user.name)}>Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* EDIT MODAL */}
            {editingUser && (
                <div className="modal-overlay">
                    <div className="modal-content admin-edit-modal">
                        <div className="modal-header">
                            <h2>Edit User & History</h2>
                            <button onClick={() => setEditingUser(null)}>&times;</button>
                        </div>
                        
                        <div className="admin-modal-grid">
                            {/* --- FORM SECTION --- */}
                            <form onSubmit={handleSave} className="admin-edit-form">
                                <h3>Profile Details</h3>
                                <label>Full Name</label>
                                <input type="text" value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})} />
                                
                                <label>Email</label>
                                <input type="email" value={editingUser.email} onChange={e => setEditingUser({...editingUser, email: e.target.value})} />

                                <label>Phone</label>
                                <input type="text" value={editingUser.phoneNumber} onChange={e => setEditingUser({...editingUser, phoneNumber: e.target.value})} />

                                <div className="row">
                                    <div><label>Gender</label><input type="text" value={editingUser.gender} onChange={e => setEditingUser({...editingUser, gender: e.target.value})} /></div>
                                    <div><label>Age</label><input type="number" value={editingUser.age} onChange={e => setEditingUser({...editingUser, age: e.target.value})} /></div>
                                </div>

                                <label>Date of Birth</label>
                                <input type="date" value={editingUser.dob} onChange={e => setEditingUser({...editingUser, dob: e.target.value})} />

                                <h3>Financials</h3>
                                <div className="row">
                                    <div><label>Wallet (₹)</label><input type="number" value={editingUser.walletBalance} onChange={e => setEditingUser({...editingUser, walletBalance: e.target.value})} /></div>
                                    <div><label>Coins</label><input type="number" value={editingUser.coins} onChange={e => setEditingUser({...editingUser, coins: e.target.value})} /></div>
                                </div>
                            <label>Role</label>
                            <select value={editingUser.isBusiness} onChange={e => setEditingUser({...editingUser, isBusiness: e.target.value === 'true'})}>
                                <option value="false">Regular User</option>
                                <option value="true">Business Account</option>
                            </select>

                            <button type="submit" className="button-primary" style={{marginTop: '20px', width: '100%'}}>Save All Changes</button>
                            </form>

                            {/* --- HISTORY SECTION --- */}
                            <div className="admin-user-history">
                                <h3>Withdrawal History</h3>
                                {loadingHistory ? <p>Loading history...</p> : (
                                    <div className="history-list-mini">
                                        {userHistory.length === 0 ? <p>No history found.</p> : userHistory.map(h => (
                                            <div key={h.id} className="history-card-mini">
                                                <span>₹{h.amount}</span>
                                                <span className={`status-pill ${h.status}`}>{h.status}</span>
                                                <small>{new Date(h.createdAt?.seconds * 1000).toLocaleDateString()}</small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersTab;